import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatLegacyCheckbox as MatCheckbox, MatLegacyCheckboxDefaultOptions as MatCheckboxDefaultOptions, MAT_LEGACY_CHECKBOX_DEFAULT_OPTIONS as MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/legacy-checkbox';
import { WizFocusManager } from '../../../../services/core/focus/wiz-focus-manager';
import { BASE_COMPONENT, WizBaseComponent } from '../../../core/wiz-base-component';
import { WizTranslatableComponent } from '../../../core/wiz-translatable-component';
import { IWIZ_COMPONENT} from '../../../../interfaces/iwiz-component';
import { VALUE_SPACE, WIZ_BOOLEAN, WIZ_EVENT_TYPE, WIZ_LATERAL, WizBooleanUtils } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../../events/wiz-event';
import { WizEventManager } from '../../../../events/wiz-event.manager';

@Component({
  selector: 'wiz-checkbox',
  templateUrl: './wiz-checkbox.html',
  styleUrls: ['./wiz-checkbox.scss'],
  providers: [
    // neutralise le 1er clic non interprété sur la check
    { provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions },
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizCheckbox) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizCheckbox) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizCheckbox) },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

/** Checkbox based on Material framework, gestion des 3 états, du skinning et de l'héritage baseComponent */
export class WizCheckbox extends WizTranslatableComponent {
  // emetteur pour pose de listener direct sur html (wizClick) = myMethod($event) => WizEvent
  @Output() wizClick: EventEmitter<WizEvent> = new EventEmitter<WizEvent>();

  @ViewChild('innerCheckbox', { static: true }) innerCheckbox: MatCheckbox; // le composant véritable

  /** Constructor */
  constructor(protected cdref: ChangeDetectorRef, public elementRef: ElementRef, public renderer: Renderer2) {
    super(cdref, elementRef, renderer);
    this.elementRef.nativeElement.addEventListener('keyup', (e: KeyboardEvent) => {
      if (e.key === VALUE_SPACE) {
        // écouteur sur space
        this.toggle();
      }
    });
    this._wizValue = this.defaultValue();
    this.checkStyle(); // application du style initial
  }

  public disabledClassName: string = 'disabled-class'; // class css pour skin "disabled"

  // ####
  // ####
  // #### configuration ####
  // ####
  // ####

  @Input() // activation du mode 3 états (true / false / null)
  public set wizTriStateMode(v: boolean) {
    this.setWizTriStateMode(v);
  }
  public get wizTriStateMode(): boolean {
    return this.getWizTriStateMode();
  }
  private _wizTriStateMode: boolean = false;

  /**
   * methodes getter/setter surchargeables de TriStateMode
   */
  protected setWizTriStateMode(value: boolean): void {
    this._wizTriStateMode = value;
    this.cdref.markForCheck();
  }
  protected getWizTriStateMode(): boolean {
    return this._wizTriStateMode;
  }

  /** label position (before/after), constante WIZ_LATERAL */
  @Input() public set wizLabelPosition(position: any) {
    this.setWizLabelPosition(position);
  }
  public get wizLabelPosition() {
    return this.getWizLabelPosition();
  }
  private _wizLabelPosition: string = WIZ_LATERAL.AFTER;
  /**
   * methodes getter/setter surchargeables de WizLabelPosition
   */
  protected setWizLabelPosition(position: any): void {
    this._wizLabelPosition = position;
    this.cdref.markForCheck();
  }
  protected getWizLabelPosition(): any {
    return this._wizLabelPosition;
  }

  /** alimentation en mode ternaire */
  @Input() public set wizCheckState(state: WIZ_BOOLEAN) {
    this.setWizCheckState(state);
  }
  public get wizCheckState(): WIZ_BOOLEAN {
    return this.getWizCheckState();
  }
  /**
   * methodes getter/setter surchargeables de WizCheckState
   */
  protected setWizCheckState(state: WIZ_BOOLEAN): void {
    this.setWizValue(state);
  }
  protected getWizCheckState(): WIZ_BOOLEAN {
    return this.getWizValue();
  }

  /** alimentation externe en mode boolean (true / false) */
  @Input() public set wizChecked(selected: boolean) {
    this.setWizChecked(selected);
  }
  public get wizChecked(): boolean {
    return this.getWizChecked();
  }
  /**
   * methodes getter/setter surchargeables de WizChecked
   */
  protected setWizChecked(selected: boolean): void {
    if (selected) {
      this.setWizValue(WIZ_BOOLEAN.TRUE);
    } else {
      this.setWizValue(WIZ_BOOLEAN.FALSE);
    }
  }
  protected getWizChecked(): boolean {
    return this.getWizValue() === WIZ_BOOLEAN.TRUE;
  }

  // ####
  // ####
  // #### mécanique interne ####
  // ####
  // ####

  /** contrat d'interface. Fonction en mode ternaire (WIZ_BOOLEAN.xxx) */
  public setWizValue(value: any) {
    super.setWizValue(value);
    this.applyCheck();
  }

  /** Default check value according to the check mode */
  private defaultValue(): WIZ_BOOLEAN {
    return this.wizTriStateMode ? WIZ_BOOLEAN.UNKNOWN : WIZ_BOOLEAN.FALSE;
  }

  /** détermination et application de l'état */
  private applyCheck(): void {
    switch (this.getWizValue()) {
      case WIZ_BOOLEAN.TRUE: // checked
        this.innerCheckbox.indeterminate = false;
        this.innerCheckbox.checked = true;
        break;
      case WIZ_BOOLEAN.FALSE: // unchecked
        this.innerCheckbox.checked = false;
        this.innerCheckbox.indeterminate = false;
        break;
      // case null:
      // case undefined:
      case WIZ_BOOLEAN.UNKNOWN: // indeterminate
      default:
        this.innerCheckbox.indeterminate = true;
        break;
    }
    this.cdref.markForCheck(); // refresh visuel
  }

  /** sur clic / space / enter, changement d'état */
  private toggle(): void {
    if (this.wizDisabled) {
      return;
    }
    // console.log('toggle before',this.wizCheckState, this.wizChecked,this.getWizValue());
    if (this.wizTriStateMode) {
      // mode ternaire
      this.wizCheckState = WizBooleanUtils.nextValue(this.wizCheckState);
    } else {
      // mode binaire
      this.wizChecked = !this.wizChecked;
    }
    // this.applyCheck();// appelé par le setWizValue (lui-même appelé par set wizCheckState/ wizChecked  )

    // dispatch d'event
    const evt: WizEvent = new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this.wizChecked);
    if (this.wizTriStateMode) {
      // surchage si mode ternaire
      evt.value = this.wizCheckState;
    }
    WizEventManager.dispatchEvent(this.elementRef, evt);
    this.wizClick.emit(evt); // dispatch direct pour écoute du wizClick html
  }

  /** MatCheckbox Click handler */
  public onClick(): void {
    // console.log('on clic', this.wizLabel, this.getWizValue());
    if (!this.wizDisabled) {
      if (!WizFocusManager.getInstance().iHaveFocus(this)) this.wizGetFocus(); // TODO setter le focus depuis label (marche pas)
      this.toggle();
    }
  }
  /** Neutralisation  clic & focus sur le label */
  public preventLabel(): void {
    // console.log('on clic label', this.wizLabel, this.getWizValue());
    // neutralisation
    if (!this.wizDisabled) {
      if (!WizFocusManager.getInstance().iHaveFocus(this)) this.wizGetFocus(); // TODO setter le focus depuis label (marche pas)
      this.wizFocus();
    }
  }

  /** contrat d'interface focusManager */
  public wizFocus(): void {
    this.innerCheckbox.focus();
  }


  /** application du style effectif (disabled, autres...) */
  public checkStyle(): void {
    // déclenché par des méthodes internes (wizDisabled...)
    if (this.wizDisabled) {
      // inactif
      this.renderer.addClass(this.elementRef.nativeElement, this.disabledClassName);
    } else {
      // actif
      this.renderer.removeClass(this.elementRef.nativeElement, this.disabledClassName);
    }
  }

  /** contrat d'interface gestion des erreurs */
  public getValidators() {
    return [];
  }
  /** contrat d'interface gestion des erreurs */
  getErrorVCRef(): ViewContainerRef {
    return null;
  }

}
